import React, {createContext, useContext, useState} from 'react';
import {message} from 'antd';
import api from '../services/api';
import {formatPath} from '../constants/PathRoutes';
import ENDPOINTS from '../constants/endpoints';
import useDefaultRequest from '../hooks/useDefaultRequest';

const ShopContext = createContext(null);

const EMPTY_CART = {items: [], total: '0.00', item_count: 0};

export const useShop = () => {
    const context = useContext(ShopContext);
    if (!context) {
        throw new Error('useShop deve ser usado dentro de ShopProvider');
    }
    return context;
};

export const useCart = () => useShop();

export const ShopProvider = ({children}) => {
    const {data: categories, isLoading: loadingCategories} = useDefaultRequest({
        url: ENDPOINTS.ECOMMERCE_CATEGORY_PUBLIC,
        dataDefault: [],
    });

    const [cart, setCart] = useState(EMPTY_CART);

    const {data: countData, refetch: refetchCount} = useDefaultRequest({
        url: ENDPOINTS.ECOMMERCE_CART_COUNT,
        dataDefault: {count: 0},
    });

    const cartCount = countData?.count ?? 0;

    const fetchCart = async () => {
        try {
            const response = await api.get(ENDPOINTS.ECOMMERCE_CART);
            setCart(response.data);
            return response.data;
        } catch {
            setCart(EMPTY_CART);
        }
    };

    const addToCart = async (product, quantity = 1) => {
        try {
            const response = await api.post(ENDPOINTS.ECOMMERCE_CART_ADD_ITEM, {
                product_id: product.id,
                quantity,
            });
            setCart(response.data);
            refetchCount();
            message.success(`"${product.name}" adicionado ao carrinho`);
        } catch {
            message.error('Erro ao adicionar item ao carrinho');
        }
    };

    const removeFromCart = async (itemId) => {
        try {
            const response = await api.delete(
                formatPath(ENDPOINTS.ECOMMERCE_CART_REMOVE_ITEM, {id: itemId})
            );
            setCart(response.data);
            refetchCount();
        } catch {
            message.error('Erro ao remover item do carrinho');
        }
    };

    const updateQuantity = async (itemId, quantity) => {
        if (quantity <= 0) {
            return removeFromCart(itemId);
        }
        try {
            const response = await api.patch(
                formatPath(ENDPOINTS.ECOMMERCE_CART_UPDATE_ITEM, {id: itemId}),
                {quantity}
            );
            setCart(response.data);
        } catch {
            message.error('Erro ao atualizar quantidade');
        }
    };

    const clearCart = async () => {
        try {
            const response = await api.delete(ENDPOINTS.ECOMMERCE_CART_CLEAR);
            setCart(response.data);
            refetchCount();
        } catch {
            message.error('Erro ao limpar carrinho');
        }
    };

    const cartItems = cart.items || [];
    const getSubtotal = () => parseFloat(cart.total || 0);
    const getCartTotal = (shipping = 0, discount = 0) => getSubtotal() + shipping - discount;
    const isInCart = (productId) => cartItems.some(item => item.product === productId);
    const getItemQuantity = (productId) => {
        const item = cartItems.find(i => i.product === productId);
        return item ? item.quantity : 0;
    };

    const value = {
        categories,
        loadingCategories,
        cart,
        cartItems,
        cartCount,
        fetchCart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getSubtotal,
        getCartTotal,
        isInCart,
        getItemQuantity,
    };

    return (
        <ShopContext.Provider value={value}>
            {children}
        </ShopContext.Provider>
    );
};